import React, { useState } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import { Buildings, CalendarBlank, Clock, Users } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CorporatePage = ({ user }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_email: user?.email || '',
    contact_phone: '',
    event_date: '',
    event_time: '',
    meal_type: 'veg-meal',
    quantity: 10,
    custom_requirements: '',
  });
  const [loading, setLoading] = useState(false);

  const mealTypes = [
    { value: 'light-snacks', label: 'Light Snacks Box', price: 150, description: 'Sandwich, juice, banana, water' },
    { value: 'veg-meal', label: 'Veg Meal Box', price: 200, description: 'Rice, dal, vegetables, roti, salad' },
    { value: 'non-veg-meal', label: 'Non-Veg Meal Box', price: 250, description: 'Chicken, rice, vegetables, roti' },
  ];

  const selectedMeal = mealTypes.find((m) => m.value === formData.meal_type);
  const totalPrice = selectedMeal ? selectedMeal.price * formData.quantity : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/corporate/order`,
        formData,
        { withCredentials: true }
      );
      toast.success('Order placed successfully! We will contact you shortly.');
      setFormData({
        company_name: '',
        contact_email: user?.email || '',
        contact_phone: '',
        event_date: '',
        event_time: '',
        meal_type: 'veg-meal',
        quantity: 10,
        custom_requirements: '',
      });
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Header user={user} />

      <main className="px-6 md:px-12 lg:px-24 py-12" data-testid="corporate-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Buildings size={40} weight="duotone" className="text-[#1A4331]" />
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading" data-testid="corporate-heading">Corporate Catering</h1>
          </div>
          <p className="text-base leading-relaxed text-stone-600 max-w-2xl">Order healthy, fresh meals in bulk for your office events, meetings, and gatherings.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-2 bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="order-form">
            <h2 className="text-xl sm:text-2xl font-medium text-[#1C1917] mb-6 font-heading">Place Your Order</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Company Name *</label>
                <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="company-name-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Contact Email *</label>
                  <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Contact Phone *</label>
                  <input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="contact-phone-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Event Date *</label>
                  <input type="date" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="event-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Event Time *</label>
                  <input type="time" value={formData.event_time} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="event-time-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Meal Type *</label>
                <div className="space-y-3">
                  {mealTypes.map((meal) => (
                    <label key={meal.value} className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.meal_type === meal.value ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-stone-200 hover:border-stone-300'}`}>
                      <input type="radio" name="meal_type" value={meal.value} checked={formData.meal_type === meal.value} onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })} className="mt-1 mr-3" data-testid={`meal-type-${meal.value}`} />
                      <div className="flex-1">
                        <div className="font-medium text-[#1C1917]">{meal.label}</div>
                        <div className="text-sm text-stone-600">{meal.description}</div>
                        <div className="text-sm font-medium text-[#1A4331] mt-1">₹{meal.price} per box</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Quantity *</label>
                <input type="number" min="10" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="quantity-input" />
                <p className="text-xs text-stone-500 mt-1">Minimum order: 10 boxes</p>
              </div>

              <div>
                <label className="block text-sm font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Custom Requirements (Optional)</label>
                <textarea value={formData.custom_requirements} onChange={(e) => setFormData({ ...formData, custom_requirements: e.target.value })} rows="3" className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" placeholder="Any special dietary requirements..." data-testid="custom-requirements-input" />
              </div>

              <button type="submit" disabled={loading} className="w-full bg-[#1A4331] text-white px-6 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all duration-200 disabled:opacity-50" data-testid="submit-order-button">
                {loading ? 'Placing Order...' : 'Place Order'}
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 h-fit sticky top-24" data-testid="order-summary">
            <h3 className="text-xl font-medium text-[#1C1917] mb-4 font-heading">Order Summary</h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-stone-600">
                <span>Meal Type:</span>
                <span className="font-medium text-[#1C1917]">{selectedMeal?.label}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Quantity:</span>
                <span className="font-medium text-[#1C1917]">{formData.quantity} boxes</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Price per box:</span>
                <span className="font-medium text-[#1C1917]">₹{selectedMeal?.price}</span>
              </div>
              <div className="border-t border-stone-200 pt-3 flex justify-between">
                <span className="font-medium text-[#1C1917]">Total Amount:</span>
                <span className="text-xl font-semibold text-[#1A4331]">₹{totalPrice.toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-sm text-stone-600"><strong>Note:</strong> GST invoice will be generated after order confirmation. Our team will contact you within 24 hours.</p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default CorporatePage;